import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Logo, { DOCKHTA_LOGOS } from '@/components/Logo';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Connexion réussie !');
      
      switch (user.role) {
        case 'patient':
          navigate('/dashboard');
          break;
        case 'professional':
          navigate('/pro/dashboard');
          break;
        case 'agent':
          navigate('/agent/dashboard');
          break;
        case 'admin':
          navigate('/admin/dashboard');
          break;
        default:
          navigate('/dashboard');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Identifiants incorrects';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6">
        <Link to="/" data-testid="login-logo">
          <Logo size="sm" />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 pb-8">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl rounded-2xl md:rounded-3xl overflow-hidden">
            {/* Header gradient bar */}
            <div className="h-2 bg-gradient-to-r from-[#E53935] via-[#2196F3] to-[#1A237E]" />
            
            <CardHeader className="text-center pt-6 md:pt-8 pb-2 px-4 md:px-6">
              <CardTitle 
                className="text-xl md:text-2xl lg:text-3xl font-bold text-[#1A237E]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Bienvenue
              </CardTitle>
              <CardDescription className="text-sm md:text-base text-[#64748B]">
                Connectez-vous à votre compte Dôckhta
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 md:px-6 lg:px-8 pt-4 md:pt-6 pb-6 md:pb-8">
              {/* Google Login Button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full h-11 md:h-12 mb-5 md:mb-6 flex items-center justify-center gap-2 md:gap-3 border-slate-200 hover:bg-slate-50 rounded-xl transition-all hover:-translate-y-0.5 text-sm md:text-base"
                data-testid="google-login-button"
              >
                {googleLoading ? (
                  <span className="w-4 h-4 md:w-5 md:h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                ) : (
                  <>
                    <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continuer avec Google
                  </>
                )}
              </Button>

              {/* Divider */}
              <div className="relative mb-5 md:mb-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-[#64748B] font-medium">ou</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-[#1A237E]">
                    Adresse e-mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#64748B]" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@exemple.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 md:pl-11 h-11 md:h-12 rounded-xl border-slate-200 focus:border-[#2196F3] focus:ring-[#2196F3]/20 text-sm md:text-base"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-[#1A237E]">
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#64748B]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="********"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 md:pl-11 pr-10 md:pr-11 h-11 md:h-12 rounded-xl border-slate-200 focus:border-[#2196F3] focus:ring-[#2196F3]/20 text-sm md:text-base"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1A237E] transition-colors p-1"
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 md:w-5 md:h-5" /> : <Eye className="w-4 h-4 md:w-5 md:h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="rounded border-slate-300 text-[#E53935] focus:ring-[#E53935]" 
                    />
                    <span className="text-xs md:text-sm text-[#64748B]">Se souvenir de moi</span>
                  </label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs md:text-sm text-[#E53935] hover:text-[#D32F2F] font-medium transition-colors"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 md:h-12 bg-[#E53935] hover:bg-[#D32F2F] text-white font-semibold rounded-xl transition-all hover:-translate-y-0.5 shadow-sm text-sm md:text-base"
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 md:w-5 md:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connexion...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Se connecter
                      <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 md:mt-8 text-center">
                <p className="text-sm md:text-base text-[#64748B]">
                  Pas encore de compte?{' '}
                  <Link 
                    to="/register" 
                    className="text-[#2196F3] hover:text-[#1E88E5] font-semibold transition-colors"
                  >
                    Créer un compte
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Login;
